import { Accordion, AccordionDetails, Alert, Box, IconButton, Skeleton, Tab, Tabs } from "@mui/material";
import { displayNotification } from "helpers";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Heading from "../../core/Heading";
import AccordionSummaryCustom from "../../AccordionSummaryCustom";
import { FaChevronDown, FaInfoCircle, FaLock, FaUnlock } from "react-icons/fa";
import CustomTabPanel from "../../CustomTabPanel";
import DynamicListField from "./DynamicListField";
import DocumentsSelectorField from "../../core/DocumentsSelectorField";
import TextModal from "../modals/TextModal";
import CommentModal from "../modals/CommentModal";
import DynamicCCLOATable from "./DynamicCCLOATable";
import DynamicCCFindings from "./DynamicCCFindings";
import { useGetCCDataQuery, useGetCCFromTemplateQuery } from "modules/pvs_activities/redux/service/dForm";
import { FormDataContext } from "../../contexts/FormContext";
import { defaultSingleCCDataTemplate } from "modules/pvs_activities/constants";
import {
    useGetCCPropertiesQuery,
    useSaveCCPropertyMutation,
} from "modules/pvs_activities/redux/service/formCCProperties";

const DynamicCCTable = (props) => {
    const formDataContext = useContext(FormDataContext);
    const siteLanguage = useSelector((s) => s.general.siteLanguage);

    const ccListRes = useGetCCFromTemplateQuery({
        formId: formDataContext.templateId,
        languageId: 3,
        missionId: formDataContext.missionId,
    });
    const ccDataRes = useGetCCDataQuery(
        {
            formId: formDataContext.templateId,
            elementId: props.element?.id ?? "",
            movementId: formDataContext.movementId,
            tagName: "CCLOAChange",
        },
        { skip: formDataContext.mode === "preview" }
    );
    const ccPropertiesRes = useGetCCPropertiesQuery(
        { elementId: props.element?.id ?? "", movementId: formDataContext.movementId },
        { skip: formDataContext.mode === "preview" }
    );
    const [saveCCProperty, saveCCPropertyRes] = useSaveCCPropertyMutation();
    const [isExpanded, setIsExpanded] = useState({});
    const [currentTab, setCurrentTab] = useState({});
    const [currentComponentTab, setCurrentComponentTab] = useState("");
    const [isLockedTmp, setIsLockedTmp] = useState({});

    useEffect(() => {
        if (ccListRes.isSuccess) {
            const tmp = {};
            const tmp1 = {};
            const tmp2 = {};
            for (const x of ccListRes.data) {
                tmp[x.id] = false;
                tmp1[x.id] = 0;
                tmp2[x.id] = false;
            }
            setIsExpanded(tmp);
            setCurrentTab(tmp1);
            setIsLockedTmp(tmp2);
        }
    }, [ccListRes]);

    useEffect(() => {
        if (saveCCPropertyRes.isError) {
            displayNotification("error", `error locking/unlocking critical competency.`);
            console.error({ error: saveCCPropertyRes.error });
        }
        if (saveCCPropertyRes.isSuccess) {
            displayNotification("success", `Successfully locked/unlocked critical competency.`);
        }
    }, [saveCCPropertyRes]);

    const ccList = useMemo(() => {
        if (ccListRes.isSuccess) return [...ccListRes.data];
        return [];
    }, [ccListRes]);

    const ccData = useMemo(() => {
        if (ccDataRes.isSuccess) return { ...ccDataRes.data };
        return [];
    }, [ccDataRes]);

    const canUnlock = useCallback(
        (ccId) => {
            if (formDataContext.mode === "preview") return true;
            if (ccPropertiesRes.isSuccess) return ccPropertiesRes.data[ccId]?.is_locked?.can_unlock ?? false;
            return false;
        },
        [ccPropertiesRes, formDataContext.mode]
    );


    const isLocked = useCallback(
        (ccId) => {
            if (formDataContext.mode === "preview") return isLockedTmp[ccId];
            if (ccPropertiesRes.isSuccess) return ccPropertiesRes.data[ccId]?.is_locked?.content ?? false;
            return false;
        },
        [ccPropertiesRes, formDataContext.mode, isLockedTmp]
    );

    const handleChange = (panel) => (event, hasExpanded) => {
        const tmp = { ...isExpanded };
        tmp[panel] = hasExpanded;
        setIsExpanded(tmp);
    };

    const componentTabs = useMemo(() => {
        const tmp = [];
        for (const x of ccList) {
            if (tmp.findIndex((r) => r.id === x.component_id) === -1) {
                tmp.push({
                    id: x.component_id,
                    name: x.component_name,
                });
            }
        }
        tmp.length > 0 && setCurrentComponentTab(tmp[0].id);
        return tmp;
    }, [ccList]);

    const getCCByComponent = useCallback(
        (component_id) => {
            return ccList.filter((r) => parseInt(r.component_id) === parseInt(component_id) && r.is_cc === 1);
        },
        [ccList]
    );

    const getCCParentObj = useCallback(
        (competency_id) => {
            const ret = ccList.find((r) => r.competency_id === competency_id && r.is_cc === 0);
            return (
                ret ?? {
                    id: "",
                    complete_name: "",
                    definition_text: "",
                }
            );
        },
        [ccList]
    );

    const handleCCLock = (ccId) => {
        const wasLocked = isLocked(ccId);
        if (wasLocked && !canUnlock(ccId)) {
            displayNotification("warning", `You don't have permissions to unlock critical competency.`);
            return;
        }
        if (!window.confirm(`You are about to ${wasLocked ? "unlock" : "lock"} critical competency. Are you sure?`)) {
            return;
        }
        if (formDataContext.mode === "preview") {
            setIsLockedTmp({ ...isLockedTmp, [ccId]: !wasLocked });
            displayNotification("success", `Successfully locked/unlocked critical competency.`);
        } else {
            saveCCProperty({
                template_id: formDataContext.templateId,
                section_id: formDataContext.sectionId,
                element_id: props.element.id,
                movement_id: formDataContext.movementId,
                cc_id: ccId,
                key: "is_locked",
                content: !wasLocked,
            });
        }
        if (isExpanded[ccId] === true) {
            setIsExpanded({ ...isExpanded, [ccId]: false });
        }
    };

    return ccListRes.isLoading ? (
        <>
            <Skeleton variant="rounded" width={"100%"} height={80} sx={{ my: 1 }} />
            <Skeleton variant="rounded" width={"100%"} height={80} sx={{ mb: 1 }} />
            <Skeleton variant="rounded" width={"100%"} height={80} sx={{ mb: 1 }} />
        </>
    ) : ccListRes.isError ? (
        <Alert severity="error">Error Loading Critical Competencies.</Alert>
    ) : ccListRes.isSuccess ? (
        <>
            <Tabs
                value={parseInt(currentComponentTab)}
                onChange={(e, tabId) => setCurrentComponentTab(tabId)}
                aria-label="Component Tabs"
                centered
                sx={{ mb: 1 }}
            >
                {componentTabs.map((r) => (
                    <Tab key={r.id} value={r.id} label={r.name} />
                ))}
            </Tabs>
            {componentTabs.map((com_id) => (
                <CustomTabPanel key={com_id.id} value={parseInt(currentComponentTab)} index={com_id.id}>
                    {getCCByComponent(com_id.id).map((r) => {
                        const parentCC = getCCParentObj(r.competency_id.split(".")[0]);
                        return (
                            <Accordion key={r.id} expanded={isExpanded[r.id] === true} onChange={handleChange(r.id)}>
                                <Box sx={{ display: "flex" }}>
                                    <Box sx={{ display: "flex", width: "100%", minWidth: "50px", maxWidth: "50px" }}>
                                        <IconButton
                                            color={!isLocked(r.id) ? "primary" : "black"}
                                            size="small"
                                            className="m-auto"
                                            onClick={() => handleCCLock(r.id)}
                                            disabled={saveCCPropertyRes.isLoading}
                                        >
                                            {!isLocked(r.id) ? <FaUnlock /> : <FaLock />}
                                        </IconButton>
                                    </Box>
                                    <AccordionSummaryCustom
                                        expandIcon={<FaChevronDown />}
                                        aria-controls="panel4-content"
                                        id="panel4-header"
                                        sx={{ pl: 0, flexGrow: 1 }}
                                        disabled={isLocked(r.id)}
                                    >
                                        <span className="align-middle">
                                            {siteLanguage === "3"
                                                ? r.complete_name_en
                                                : siteLanguage === "4"
                                                ? r.complete_name_fr
                                                : siteLanguage === "5"
                                                ? r.complete_name_es
                                                : r.complete_name}
                                        </span>
                                        {ccData[r.id]?.loa_score?.content && (
                                            <span className="align-middle ms-auto me-2 text-primary">
                                                (LOA Score: {ccData[r.id]?.loa_score?.content})
                                            </span>
                                        )}
                                    </AccordionSummaryCustom>
                                </Box>
                                <AccordionDetails sx={{ borderTop: "1px solid rgba(0, 0, 0, .125)" }}>
                                    {isExpanded[r.id] === true && (
                                        <>
                                            <div className="d-flex my-2 align-items-center">
                                                <Heading
                                                    size="h6"
                                                    font="Sohne-Buch"
                                                    label={
                                                        siteLanguage === "3"
                                                            ? parentCC.complete_name_en
                                                            : siteLanguage === "4"
                                                            ? parentCC.complete_name_fr
                                                            : siteLanguage === "5"
                                                            ? parentCC.complete_name_es
                                                            : parentCC.complete_name
                                                    }
                                                />
                                                <div className="ms-auto">
                                                    <TextModal
                                                        title="Sources"
                                                        icon={<FaInfoCircle />}
                                                        content={
                                                            siteLanguage === "3"
                                                                ? props.element?.properties[
                                                                      `cc_${r.id}_sources_english`
                                                                  ]?.content
                                                                : siteLanguage === "4"
                                                                ? props.element?.properties[`cc_${r.id}_sources_french`]
                                                                      ?.content
                                                                : siteLanguage === "5"
                                                                ? props.element?.properties[
                                                                      `cc_${r.id}_sources_spanish`
                                                                  ]?.content
                                                                : "No Data"
                                                        }
                                                        className="me-1"
                                                        size={"small"}
                                                    />
                                                    <TextModal
                                                        title="Code"
                                                        icon={<FaInfoCircle />}
                                                        content={
                                                            siteLanguage === "3"
                                                                ? props.element?.properties[`cc_${r.id}_code_english`]
                                                                      ?.content
                                                                : siteLanguage === "4"
                                                                ? props.element?.properties[`cc_${r.id}_code_french`]
                                                                      ?.content
                                                                : siteLanguage === "5"
                                                                ? props.element?.properties[`cc_${r.id}_code_spanish`]
                                                                      ?.content
                                                                : "No Data"
                                                        }
                                                        className="me-1"
                                                        size={"small"}
                                                    />
                                                    <TextModal
                                                        title="Scope"
                                                        icon={<FaInfoCircle />}
                                                        content={
                                                            siteLanguage === "3"
                                                                ? props.element?.properties[`cc_${r.id}_scope_english`]
                                                                      ?.content
                                                                : siteLanguage === "4"
                                                                ? props.element?.properties[`cc_${r.id}_scope_french`]
                                                                      ?.content
                                                                : siteLanguage === "5"
                                                                ? props.element?.properties[`cc_${r.id}_scope_spanish`]
                                                                      ?.content
                                                                : "No Data"
                                                        }
                                                        className="me-1"
                                                        size={"small"}
                                                    />
                                                    <CommentModal elementId={props.id} ccId={r.id} className="ms-1" />
                                                </div>
                                            </div>
                                            <DynamicCCLOATable
                                                elementId={props.element.id}
                                                ccId={r.id}
                                                ccData={r}
                                                value={ccData[r.id]?.loa_score ?? defaultSingleCCDataTemplate}
                                                hasParent={parentCC?.id !== "" ? true : false}
                                                parentCCDefinitionText={parentCC.definition_text}
                                                disabled={isLocked(r.id)}
                                            />
                                            <Tabs
                                                value={parseInt(currentTab[r.id])}
                                                onChange={(e, tabIndex) =>
                                                    setCurrentTab({ ...currentTab, [r.id]: tabIndex })
                                                }
                                                aria-label="CC Tabs"
                                                centered
                                            >
                                                <Tab label="Evidences" />
                                                <Tab label="Findings" />
                                                <Tab label="Strengths" />
                                                <Tab label="Weaknessess" />
                                                <Tab label="Recommendations" />
                                            </Tabs>
                                            <hr className="mt-0 mb-4" />
                                            <CustomTabPanel value={currentTab[r.id]} index={0}>
                                                <DocumentsSelectorField
                                                    elementId={props.element.id}
                                                    ccId={r.id}
                                                    element={props.element}
                                                    label="Evidences:"
                                                    value={ccData[r.id]?.evidence ?? []}
                                                    disabled={isLocked(r.id)}
                                                    isFocused={currentTab[r.id] === 0}
                                                />
                                            </CustomTabPanel>
                                            <CustomTabPanel value={currentTab[r.id]} index={1}>
                                                <DynamicCCFindings
                                                    elementId={props.element.id}
                                                    ccId={r.id}
                                                    value={ccData[r.id]?.findings ?? defaultSingleCCDataTemplate}
                                                    disabled={isLocked(r.id)}
                                                    isFocused={currentTab[r.id] === 1}
                                                />
                                            </CustomTabPanel>
                                            <CustomTabPanel value={currentTab[r.id]} index={2}>
                                                <DynamicListField
                                                    elementId={props.element.id}
                                                    ccId={r.id}
                                                    label="Strengths"
                                                    listKey="strengths"
                                                    value={ccData[r.id]?.strengths ?? []}
                                                    disabled={isLocked(r.id)}
                                                    type={"Strengths"}
                                                    isFocused={currentTab[r.id] === 2}
                                                />
                                            </CustomTabPanel>
                                            <CustomTabPanel value={currentTab[r.id]} index={3}>
                                                <DynamicListField
                                                    elementId={props.element.id}
                                                    ccId={r.id}
                                                    label="Weaknesses"
                                                    listKey="weaknesses"
                                                    value={ccData[r.id]?.weaknesses ?? []}
                                                    disabled={isLocked(r.id)}
                                                    type={"Weaknesses"}
                                                    isFocused={currentTab[r.id] === 3}
                                                />
                                            </CustomTabPanel>
                                            <CustomTabPanel value={currentTab[r.id]} index={4}>
                                                <DynamicListField
                                                    elementId={props.element.id}
                                                    ccId={r.id}
                                                    label="Recommendations"
                                                    listKey="recommendations"
                                                    value={ccData[r.id]?.recommendations ?? []}
                                                    disabled={isLocked(r.id)}
                                                    type={"Recommendations"}
                                                    isFocused={currentTab[r.id] === 4}
                                                />
                                            </CustomTabPanel>
                                        </>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </CustomTabPanel>
            ))}
        </>
    ) : (
        ""
    );
};

DynamicCCTable.defaultProps = {
    id: "",
    element: {},
};

export default DynamicCCTable;
